import React from 'react';
import './App.css';
import AudioPreview from './editor/AudioPreview';
import AdvancedAudioEditor from './editor/AdvancedAudioEditor';
import type { LibraryFile } from './editor/types';
import { getApiUrl } from './api';

type LibraryResponse = {
  categories: { name: string }[] | string[];
  files: LibraryFile[];
};

type ThemeMode = 'light' | 'dark';

const formatBytes = (value: number) => {
  if (!Number.isFinite(value)) {
    return '--';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value: string) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
};

function App() {
  const [files, setFiles] = React.useState<LibraryFile[]>([]);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeCategory, setActiveCategory] = React.useState('All');
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = React.useState(false);
  const [theme, setTheme] = React.useState<ThemeMode>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    const stored = window.localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  React.useEffect(() => {
    document.body.dataset.theme = theme;
    window.localStorage.setItem('theme', theme);
  }, [theme]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(getApiUrl('/api/library'));
        if (!response.ok) {
          throw new Error(`Library request failed (${response.status})`);
        }
        const data = (await response.json()) as LibraryResponse;
        if (cancelled) {
          return;
        }
        setFiles(data.files ?? []);
        const categoryList = (data.categories ?? []).map((item) =>
          typeof item === 'string' ? item : item.name
        );
        setCategories(categoryList);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load library');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryOptions = React.useMemo(() => {
    const unique = new Set(categories);
    return ['All', ...Array.from(unique)];
  }, [categories]);

  const filteredFiles = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return files.filter((file) => {
      if (activeCategory !== 'All' && file.category !== activeCategory) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return (
        file.name.toLowerCase().includes(normalizedQuery) ||
        file.category.toLowerCase().includes(normalizedQuery) ||
        file.ext.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [files, activeCategory, query]);

  const activeFile =
    filteredFiles.find((file) => file.id === activeId) ?? null;

  React.useEffect(() => {
    if (activeId && !filteredFiles.some((file) => file.id === activeId)) {
      setActiveId(null);
    }
  }, [filteredFiles, activeId]);

  React.useEffect(() => {
    if (!activeFile?.isAudio) {
      setIsAdvancedOpen(false);
    }
  }, [activeFile?.id, activeFile?.isAudio]);

  const handleDownload = async (file: LibraryFile) => {
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(
        getApiUrl(`/api/download?path=${encodeURIComponent(file.path)}`)
      );
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="app">
      <header className="hero">
        <div>
          <p className="eyebrow">Kearney Archive - 1.0.3</p>
          <h1>Library</h1>
        </div>
        <div className="hero-actions">
          <button
            className="ghost theme-toggle"
            type="button"
            onClick={() =>
              setTheme((current) => (current === 'light' ? 'dark' : 'light'))
            }
            aria-label={theme === 'light' ? 'Enable dark mode' : 'Enable light mode'}
          >
            {theme === 'light' ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 4.5a1 1 0 0 1 1 1V7a1 1 0 1 1-2 0V5.5a1 1 0 0 1 1-1z" />
                <path d="M12 17a1 1 0 0 1 1 1v1.5a1 1 0 1 1-2 0V18a1 1 0 0 1 1-1z" />
                <path d="M5.5 11a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2h1.5z" />
                <path d="M20 11a1 1 0 1 1 0 2h-1.5a1 1 0 1 1 0-2H20z" />
                <path d="M7.05 7.05a1 1 0 0 1 1.41 0l1.06 1.06a1 1 0 1 1-1.41 1.41L7.05 8.46a1 1 0 0 1 0-1.41z" />
                <path d="M15.48 15.48a1 1 0 0 1 1.41 0l1.06 1.06a1 1 0 0 1-1.41 1.41l-1.06-1.06a1 1 0 0 1 0-1.41z" />
                <path d="M7.05 16.95a1 1 0 0 1 0-1.41l1.06-1.06a1 1 0 1 1 1.41 1.41l-1.06 1.06a1 1 0 0 1-1.41 0z" />
                <path d="M15.48 8.52a1 1 0 0 1 0-1.41l1.06-1.06a1 1 0 1 1 1.41 1.41l-1.06 1.06a1 1 0 0 1-1.41 0z" />
                <circle cx="12" cy="12" r="4" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14.5 3.5a8.5 8.5 0 1 0 6 14.5 7 7 0 1 1-6-14.5z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <section className="layout">
        <aside className="panel sources">
          <div className="panel-header">
            <h2>Categories</h2>
            <span className="count">{categoryOptions.length}</span>
          </div>
          <ul className="source-list">
            {categoryOptions.map((category) => (
              <li
                key={category}
                className={category === activeCategory ? 'active' : undefined}
              >
                <button type="button" onClick={() => setActiveCategory(category)}>
                  {category}
                </button>
              </li>
            ))}
          </ul>
          <div className="panel-block">
            <h3>Search</h3>
            <input
              className="search"
              type="search"
              placeholder="Search by title or tag"
              aria-label="Search archive"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </aside>

        <main className="panel archive">
          <div className="panel-header">
            <h2>Files</h2>
            <span className="count">{filteredFiles.length}</span>
          </div>
          {loading ? (
            <p className="status">Syncing with library...</p>
          ) : error ? (
            <p className="status error">{error}</p>
          ) : filteredFiles.length === 0 ? (
            <p className="status">No files match this view.</p>
          ) : (
            <div className="file-list">
              {filteredFiles.map((file, index) => (
                <button
                  className={`file-card ${activeId === file.id ? 'selected' : ''}`}
                  key={file.id}
                  type="button"
                  onClick={() => setActiveId(file.id)}
                  style={{ '--delay': `${index * 70}ms` } as React.CSSProperties}
                >
                  <div>
                    <p className="file-title">{file.name}</p>
                    <p className="file-meta">
                      {file.category} - {formatDate(file.modified)}
                    </p>
                  </div>
                  <div className="file-meta">
                    <span className="pill">{file.ext.toUpperCase()}</span>
                    <span className="pill">{formatBytes(file.size)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </main>

        <aside className="panel editor">
          <div className="panel-header">
            <h2>Editor</h2>
            <span className="count">
              {activeFile ? activeFile.ext.toUpperCase() : '--'}
            </span>
          </div>
          {activeFile ? (
            <>
              <div className="editor-details">
                <div>
                  <p className="file-title">{activeFile.name}</p>
                  <p className="file-meta">
                    Category {activeFile.category} - {formatDate(activeFile.modified)}
                  </p>
                </div>
                <div className="editor-actions">
                  <button
                    className="ghost"
                    type="button"
                    onClick={() => handleDownload(activeFile)}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </button>
                  {activeFile.isAudio ? (
                    <button
                      className="primary"
                      type="button"
                      onClick={() => setIsAdvancedOpen(true)}
                    >
                      Open in editor
                    </button>
                  ) : null}
                </div>
                {downloadError ? (
                  <p className="status error">{downloadError}</p>
                ) : null}
              </div>

              {activeFile.isAudio ? (
                <AudioPreview
                  file={activeFile}
                  themeKey={theme}
                  isActive={!isAdvancedOpen}
                />
              ) : (
                <div className="editor-body">
                  <div className="project-preview">
                    <p className="file-title">Session overview</p>
                    <p className="file-meta">
                      Stems, notes, and arrangement live in this project bundle.
                    </p>
                    <div className="project-grid">
                      <div>
                        <span className="pill">Read only</span>
                        <span className="pill">Streaming</span>
                      </div>
                      <div>
                        <span className="pill">Metadata</span>
                        <span className="pill">Preview</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="status">Select a file to preview.</p>
          )}
        </aside>
      </section>

      {activeFile && activeFile.isAudio ? (
        <AdvancedAudioEditor
          file={activeFile}
          isOpen={isAdvancedOpen}
          onClose={() => setIsAdvancedOpen(false)}
          themeKey={theme}
        />
      ) : null}
    </div>
  );
}

export default App;
